import React, { useCallback, useMemo, useEffect, useState } from "react";
import { TreeSelect, DownOutlined, Tooltip } from "src/assets/imports/antdComponents";
import { IMultiSelectStudyDropdownProps, StudyType } from "../../models/interfaces";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import "./style.css";

const MultiSelectStudyDropdown: React.FC<IMultiSelectStudyDropdownProps> = React.memo(
	({ availableStudyTypeDropdownItems, selectedType, handleTypeChange, isCompDisabled, isLoading, multiSelectLabel, disabled, disabledItems = [], isHomePage }) => {
		const { t } = useTranslation();
		const { referenceDrawerVisibility, isStandardQueryMode } = useSelector((state: RootState) => state.askKuralSlice);
		// Get all enabled options
		const allEnabledOptions = useMemo(
			() => availableStudyTypeDropdownItems.filter((item) => !item.disabled).map((item) => item.value as StudyType),
			[availableStudyTypeDropdownItems],
		);
		const [isOpen, setIsOpen] = useState(false);

		const getDisabledInfo = useCallback(
			(value) => {
				const disabledItem = disabledItems.find((item) => item.value === value);
				return {
					isDisabled: !!disabledItem,
					message: disabledItem?.message || null,
				};
			},
			[disabledItems],
		);

		// Create the tree data with "All" option that is checkable
		const treeData = useMemo(
			() => [
				{
					title: t("All"),
					value: "All",
					key: "All",
					disabled: isCompDisabled || isLoading || allEnabledOptions?.length === 0,
					children: [
						...availableStudyTypeDropdownItems.map((item) => {
							const { isDisabled, message } = getDisabledInfo(item.value);
							const isItemDisabled = item.disabled || isDisabled || isCompDisabled || isLoading;

							return {
								title: isItemDisabled ? (
									<Tooltip title={t(message)}>
										<span>{item.label}</span>
									</Tooltip>
								) : (
									item.label
								),
								value: item.value,
								disabled: isItemDisabled,
							};
						}),
					],
				},
			],
			[availableStudyTypeDropdownItems, t, isCompDisabled, isLoading, getDisabledInfo],
		);

		// Handle selection changes
		const handleChange = useCallback(
			(newValue: string[]) => {
				if (disabled) return;

				let updatedValue: any[];

				if (isHomePage) {
					// For Home Page: Just ensure it's not empty.
					// If empty, default to "All" (assuming allEnabledOptions[0] is "All")
					updatedValue = newValue.length > 0 ? newValue : [allEnabledOptions[0]];
				} else {
					// Original logic: Filter based on StudyType enum for specific app modules
					updatedValue = newValue.filter((value) => Object.values(StudyType).includes(value as StudyType)) as StudyType[];

					// Default to first option if nothing valid is left
					if (updatedValue.length === 0) {
						updatedValue = [allEnabledOptions[0]];
					}
				}

				handleTypeChange(updatedValue);
			},
			[disabled, allEnabledOptions, handleTypeChange, isHomePage], // Added isHomePage to deps
		);
		// Get the placeholder text
		const placeholderText = useMemo(() => {
			if (selectedType?.length === 0) {
				return t("Select Study Type");
			} else {
				return `${selectedType?.length} ${t("Type(s) selected")}`;
			}
		}, [selectedType, t]);

		const selectedTypeLabels = useMemo(() => {
			return selectedType
				?.map((type) => {
					const item = availableStudyTypeDropdownItems.find((item) => item.value === type);
					return item ? item.label : type;
				})
				.join(", ");
		}, [selectedType, availableStudyTypeDropdownItems]);

		return (
			<Tooltip
				getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
				title={disabled ? selectedTypeLabels : isHomePage ? "" : t("Study Type Filter")}>
				<TreeSelect
					className={`${isHomePage ? "multi-select-study-dropdown-2 " : "multi-select-study-dropdown"} ${referenceDrawerVisibility}  ${disabled ? "disabled-multi-select-study-dropdown" : ""} ${
						selectedType?.length === 0 ? "mutli-select-study-empty" : ""
					}`}
					treeData={treeData}
					value={selectedType}
					onChange={handleChange}
					multiple
					showSearch={false}
					treeCheckable
					treeCheckStrictly={false}
					getPopupContainer={(trigger) => (isHomePage ? document.body : trigger.closest(".studies-page-container") ?? document.body)}
					placement="bottomLeft"
					style={{ width: "100%" }}
					treeDefaultExpandAll={true}
					maxTagCount={0}
					maxTagPlaceholder={
						isHomePage ? (
							<div className="flex items-center gap-2">
								<span className="text-base-13 font-semibold text-[--secondary-color-2]">{t("Study Type")}</span>
							</div>
						) : (
							<div className={`flex items-center justify-center ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
								<i className={`material-symbols-outlined ${disabled ? "text-[--neutral-grey]" : ""} !text-[1.5rem]`}>page_info</i>
							</div>
						)
					}
					disabled={disabled}
					placeholder={
						<div className={`flex items-center justify-center gap-2 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
							<i className="material-symbols-outlined !text-[1.5rem]">page_info</i>
							<span className="multi-select-placeholder-text">{t(multiSelectLabel)}</span>
						</div>
					}
					popupClassName="multi-select-study-dropdown-popup"
					// suffixIcon={<DownOutlined className="text-[--secondary-color] text-bold" />}
					onDropdownVisibleChange={(open) => setIsOpen(open)}
					suffixIcon={
						isHomePage ? (
							<DownOutlined
								className="text-[--secondary-color] text-sm"
								style={{
									transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
									transition: "transform 0.2s ease",
								}}
							/>
						) : null
					}
					popupMatchSelectWidth={false}
				/>
			</Tooltip>
		);
	},
);

export default MultiSelectStudyDropdown;
